import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import DemoForm from "../components/forms/Form";
import FormMini from "../components/forms/FormMini";
import Slideshow from "../components/Slideshow";

import { getCookie } from "cookies-next";

import fonts from "../styles/FontInter.module.css";
import mainTheme from "../components/mainTheme";
import styles from "../styles/Home.module.css";
import ExitModal from "../components/ExitModal";

export default function TemplatePage({ utm }) {
  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>SRAR - Elm Street Technology</title>
        <meta
          name="description"
          content="Real estate is all about relationships. Nurturing them. Growing them. Building a close network of repeat business and referrals because of them."
        />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@weareelmstreet" />
        <meta name="twitter:creator" content="@weareelmstreet" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://elmstreet.com/repeat-and-referral" />
        <meta property="og:title" content="Southland Regional Association of Realtors (SRAR) - Elm Street Technology" />
        <meta
          property="og:description"
          content="Real estate is all about relationships. Nurturing them. Growing them. Building a close network of repeat business and referrals because of them."
        />
        <meta property="og:image" content="https://elmstreet.com/og_images/elmstreetog.jpg" />
      </Head>
      <CssBaseline className={fonts} />

      <div
        style={{
          backgroundImage: "url('img-homesmart.jpg')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
        alt="Elm Street Technology SRAR"
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={5} sm={12}>
              <div
                style={{
                  color: "#FFFFFF",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  height: "650px",
                }}
              >
                <div>
                  <div
                    style={{
                      marginBottom: "15px",
                    }}
                  >
                    <Image src="/logo-srar.jpg" width="384px" height="78px" alt="Elm Street Technology - SRAR" />
                  </div>
                  <Typography variant="h2" component="h1">
                    <span style={{ color: mainTheme.palette.secondary.main }}>REIMAGINING</span> <br />
                    THE REAL
                    <br />
                    ESTATE JOURNEY
                  </Typography>
                  <p>
                    Elm Street Technology is committed to helping our clients create lifelong relationships, making it
                    easier to buy and sell homes. We do this by providing the tools and services real estate
                    professionals need to streamline and automate their marketing and day-to-day business objectives.
                    <br />
                    <br />
                  </p>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div
        style={{
          backgroundColor: "#F7F7F7",
        }}
      >
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "50px",
            paddingBottom: "50px",
          }}
        >
          <div style={{ paddingTop: "20px", paddingBottom: "20px", textAlign: "center" }}>
            <Typography variant="h4" component="h4">
              <span style={{ color: "#f69c26" }}>Elevate your</span>
              <br />
              <span>productivity, success, and growth</span>
            </Typography>
            <p>Do it using features of Elm Street Technology.</p>
          </div>
          <Grid container columnSpacing={4}>
            <Grid item md={3} sm={12} style={{ paddingBottom: "24px", fontSize: "14px", textAlign: "center" }}>
              <span>
                <div style={{ paddingBottom: "16px" }}>
                  <Image src="/srar-img-1.png" width="203px" height="202px" alt="Homesmart" />
                </div>
                <Typography variant="h4" component="h4" style={{ paddingBottom: "16px", fontSize: "16px" }}>
                  IDX Websites
                </Typography>
                Utilize an IDX Website that integrates with other features of the Elm Street suite of solutions. Create
                a dynamic online presence by having an attractive, magnetic IDX website, utilizing high-quality,
                automated content creation to drive visitors to your website that feeds your business growth.
              </span>
            </Grid>

            <Grid item md={3} sm={12} style={{ paddingBottom: "24px", fontSize: "14px", textAlign: "center" }}>
              <span>
                <div style={{ paddingBottom: "16px" }}>
                  <Image src="/srar-img-2.png" width="203px" height="202px" paddingBottom="16px" alt="Homesmart" />
                </div>
                <Typography variant="h4" component="h4" style={{ paddingBottom: "16px", fontSize: "16px" }}>
                  Lead Generation & Concierge Service
                </Typography>
                We are here to help generate high-quality leads, and responsive leads are instantly matched with our
                human concierge team and are qualified using customizable scripts via text and phone calls 24/7. With
                our lead concierge, you can increase conversion rates by providing personalized and timely communication
                with these leads.
              </span>
            </Grid>

            <Grid item md={3} sm={12} style={{ paddingBottom: "24px", fontSize: "14px", textAlign: "center" }}>
              <span>
                <div style={{ paddingBottom: "16px" }}>
                  <Image src="/srar-img-3.png" width="203px" height="202px" paddingBottom="16px" alt="Homesmart" />
                </div>
                <Typography variant="h4" component="h4" style={{ paddingBottom: "16px", fontSize: "16px" }}>
                  Social Media Automation
                </Typography>
                Automate your social media marketing so you can focus on what matters most…growing your sphere and
                selling more homes, faster. Have customized branding on social media, provide relevant, lifestyle
                content that is posted weekly, have automated posts of new listings and listing updates and
                automatically create YouTube video tours.
              </span>
            </Grid>

            <Grid item md={3} sm={12} style={{ paddingBottom: "24px", fontSize: "14px", textAlign: "center" }}>
              <span>
                <div style={{ paddingBottom: "16px" }}>
                  <Image src="/srar-img-4.png" width="202px" height="202px" paddingBottom="16px" alt="Homesmart" />
                </div>
                <Typography variant="h4" component="h4" style={{ paddingBottom: "16px", fontSize: "16px" }}>
                  Automated Blog Content
                </Typography>
                Generate a robust blog with engaging content and automated posts. Provide content-rich consumer blog
                content with weekly, automated posts. With Elm Street’s automated blog featured, you will continue to
                stay top of mind and relevant for consumers.
              </span>
            </Grid>
          </Grid>

          <div style={{ paddingTop: "20px", paddingBottom: "80px", textAlign: "center" }}>
            <Button variant="contained" color="secondary" href="#form">
              schedule a demo
            </Button>
          </div>

          <Grid container columnSpacing={4}>
            <Grid item md={6} sm={12} style={{ paddingBottom: "24px", fontSize: "14px", textAlign: "center" }}>
              <span>
                <div style={{ paddingBottom: "16px" }}>
                  <Image src="/srar-img-5.png" width="203px" height="202px" alt="Homesmart" />
                </div>
                <Typography variant="h4" component="h4" style={{ paddingBottom: "16px", fontSize: "16px" }}>
                  CRM
                </Typography>
                An all-in-one real estate CRM that makes it easy to manage your contacts, leads, transactions, and
                marketing campaigns. With our system, get more done by streamlining your workflow, increasing your
                productivity.
              </span>
            </Grid>

            <Grid item md={6} sm={12} style={{ paddingBottom: "24px", fontSize: "14px", textAlign: "center" }}>
              <span>
                <div style={{ paddingBottom: "16px" }}>
                  <Image src="/srar-img-6.png" width="203px" height="202px" paddingBottom="16px" alt="Homesmart" />
                </div>
                <Typography variant="h4" component="h4" style={{ paddingBottom: "16px", fontSize: "16px" }}>
                  Automated e-Newsletter
                </Typography>
                Imagine the peace of mind knowing that – no matter how busy you get – a professional branded real estate
                Newsletter is going to all your contacts every month, reminding them why YOU are the real estate agent
                of choice! Stay in touch and generate leads with our done-for-you monthly e-Newsletter.
              </span>
            </Grid>
          </Grid>

          <div style={{ paddingTop: "20px", paddingBottom: "80px", textAlign: "center" }}>
            <Button variant="contained" color="secondary" href="https://www.ixactcontact.com/srar">
              SIGN UP TODAY
            </Button>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "100px",
            paddingTop: "100px",
          }}
        >
          <div style={{ marginTop: "35px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>BOOK AN EVENT</span>
                  <br />
                  <span>
                    An educational experience that INSPIRES, MOTIVATES and CREATES EXCITEMENT by teaching topics agents
                    need!
                  </span>
                </Typography>

                <p>
                  Whether you are just starting your business journey or are a seasoned professional looking to “up your
                  game” with the latest tech tools, marketing masterminds, and ongoing continuing education, Elm Street
                  Academy is focused on supporting you every step of the way. We use humor, practical advice, and
                  real-life experiences to give growth-minded real estate professionals empowering and results-driven
                  education.
                </p>
                <p>
                  Elm Street partners with growth-minded agents and offices brings FREE educational classes to your
                  brokerage.
                </p>

                <Button variant="contained" color="secondary" href="/masterclass">
                  Book Your Free In-Office Masterclasses
                </Button>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/homesmart-1.png"
                    width="929px"
                    height="980px"
                    alt="Elm Street Technology - Book an Event"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('orangebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg" style={{ paddingTop: "80px", paddingBottom: "85px" }}>
          <div style={{}}>
            <a id="form" />
            <Typography variant="h4" component="h4" style={{ color: "#1d1746", textAlign: "center" }}>
              SCHEDULE A DEMO TO LEARN ABOUT EXCLUSIVE OFFERINGS AND DISCOUNTS FOR SRAR MEMBERS
            </Typography>
            <div style={{ paddingBottom: "40px", paddingLeft: "20px", paddingRight: "20px", textAlign: "center" }}>
              <strong style={{ color: "#1d1746", textAlign: "center" }}>
                Connect with a Success Coach to see what Elm Street Technology can do for your business..
              </strong>
            </div>
            <div style={{ padding: "15px", backgroundColor: "#ffffff" }}>
              <Container
                maxWidth="lg"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "65px",
                  marginBottom: "65px",
                }}
              >
                <Grid container>
                  <Grid item md={2} sm={12} xs={12} />
                  <Grid item md={8} sm={12} xs={12}>
                    <DemoForm
                      buttonText="Schedule Demo"
                      successUrl="thankyou"
                      listId="147986"
                      formName="es2_srar"
                      fieldList={[
                        "first_name",
                        "last_name",
                        "email",
                        "phone",
                        "roll",
                        "homesmart_interests",
                        "qualifying",
                      ]}
                    />
                  </Grid>
                </Grid>
              </Container>
            </div>
          </div>
        </Container>
      </div>
    </ThemeProvider>
  );
}

export const getServerSideProps = ({ req, res }) => {
  let cookies = getCookie("elmlead", { req, res });
  if (cookies) {
    return { props: { utm: cookies } };
  }
  return { props: {} };
};
